import React from 'react';
import styled from 'styled-components';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, 
  FileText, 
  Activity, 
  Shield, 
  User,
  Menu,
  X,
  Building,
  BarChart3,
  FileBarChart
} from 'lucide-react';

interface SidebarProps {
  isOpen: boolean;
  onToggle: () => void;
}

const sidebarStyleProps = new Set(['isOpen', 'active']);

const SidebarContainer = styled.aside.withConfig({
  shouldForwardProp: (prop) => !sidebarStyleProps.has(prop),
})<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  width: ${props => props.isOpen ? '280px' : '80px'};
  background: linear-gradient(180deg, #1e3a8a 0%, #1e40af 100%);
  color: white;
  transition: width 0.3s ease;
  overflow: hidden;
  z-index: 1000;
  box-shadow: 4px 0 20px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    width: ${props => props.isOpen ? '100%' : '0'};
  }
`;

const SidebarHeader = styled.div.withConfig({
  shouldForwardProp: (prop) => !sidebarStyleProps.has(prop),
})<{ isOpen: boolean }>`
  padding: ${props => props.isOpen ? '24px 20px' : '24px 12px'};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: ${props => props.isOpen ? 'space-between' : 'center'};
`;

const Logo = styled.div.withConfig({
  shouldForwardProp: (prop) => !sidebarStyleProps.has(prop),
})<{ isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => props.isOpen ? '12px' : '0'};
  font-size: 18px;
  font-weight: 700;
  color: white;
  justify-content: ${props => props.isOpen ? 'flex-start' : 'center'};
`;

const LogoImage = styled.img.withConfig({
  shouldForwardProp: (prop) => !sidebarStyleProps.has(prop),
})<{ isOpen: boolean }>`
  width: ${props => props.isOpen ? '40px' : '32px'};
  height: ${props => props.isOpen ? '40px' : '32px'};
  object-fit: contain;
  border-radius: 8px;
  background: white;
  padding: 4px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 8px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }

  @media (min-width: 769px) {
    display: none;
  }
`;

const Navigation = styled.nav`
  padding: 20px 0;
  flex: 1;
`;

const NavItem = styled.div.withConfig({
  shouldForwardProp: (prop) => !sidebarStyleProps.has(prop),
})<{ active: boolean; isOpen: boolean }>`
  display: flex;
  align-items: center;
  gap: ${props => props.isOpen ? '12px' : '0'};
  padding: ${props => props.isOpen ? '12px 20px' : '12px'};
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.active ? 'rgba(255, 255, 255, 0.1)' : 'transparent'};
  border-right: ${props => props.active ? '3px solid #3b82f6' : '3px solid transparent'};
  color: ${props => props.active ? '#ffffff' : 'rgba(255, 255, 255, 0.8)'};
  justify-content: ${props => props.isOpen ? 'flex-start' : 'center'};

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: white;
  }
`;

const NavIcon = styled.div`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const NavText = styled.span.withConfig({
  shouldForwardProp: (prop) => !sidebarStyleProps.has(prop),
})<{ isOpen: boolean }>`
  font-size: 14px;
  font-weight: 500;
  display: ${props => props.isOpen ? 'block' : 'none'};
  white-space: nowrap;
  overflow: hidden;
`;


const Overlay = styled.div.withConfig({
  shouldForwardProp: (prop) => !sidebarStyleProps.has(prop),
})<{ isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  display: ${props => props.isOpen ? 'block' : 'none'};

  @media (min-width: 769px) {
    display: none;
  }
`;

const navigationItems = [
  { path: '/', label: 'Dashboard', icon: Home },
  { path: '/assessments', label: 'Assessments', icon: FileText },
  { path: '/facilities', label: 'Facilities', icon: Building },
  { path: '/facilities/stats', label: 'Facility Stats', icon: BarChart3 },
  { path: '/reports', label: 'Reports', icon: FileBarChart },
  { path: '/audit-logs', label: 'Audit Logs', icon: Shield },
  { path: '/profile', label: 'Profile', icon: User },
];

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onToggle }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const handleNavigation = (path: string) => {
    navigate(path);
    // Close sidebar on mobile after navigation
    if (window.innerWidth <= 768) {
      onToggle();
    }
  };

  return (
    <>
      <Overlay isOpen={isOpen} onClick={onToggle} />
      <SidebarContainer isOpen={isOpen}>
        <SidebarHeader isOpen={isOpen}>
          <Logo isOpen={isOpen}>
            <LogoImage 
              src="https://www.chcahealth.org/wp-content/uploads/2023/12/main-logop.png" 
              alt="CHCA Logo"
              isOpen={isOpen}
            />
            {isOpen && (
              <div>
                <div>CHCA</div>
                <div style={{ fontSize: '12px', opacity: 0.8 }}>LACE Tool</div>
              </div>
            )}
          </Logo>
          {isOpen && (
            <CloseButton onClick={onToggle}>
              <X size={20} />
            </CloseButton>
          )}
        </SidebarHeader>

        <Navigation>
          {navigationItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            
            return (
              <NavItem
                key={item.path}
                active={isActive}
                isOpen={isOpen}
                onClick={() => handleNavigation(item.path)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleNavigation(item.path);
                  }
                }}
                aria-label={`Navigate to ${item.label}`}
                title={!isOpen ? item.label : undefined}
              >
                <NavIcon>
                  <Icon size={20} />
                </NavIcon>
                <NavText isOpen={isOpen}>{item.label}</NavText>
              </NavItem>
            );
          })}
        </Navigation>

      </SidebarContainer>
    </>
  );
};

export default Sidebar;
